import { useState, type ComponentProps, type ReactNode } from 'react';

import { Textarea } from '../../../../components/ui';
import { cn } from '../../../../lib/cn';
import { tachDong } from '../../nghiep-vu/cau-hinh-bot';
import type { ChatbotInput } from '../../types';

/** Khung ngoài của một khối lớn ở màn cấu hình bot. */
export const KHOI = 'rounded-lg border border-default bg-surface shadow-sm';

export type DatTruong = <K extends keyof ChatbotInput>(k: K, v: ChatbotInput[K]) => void;

export interface TabProps {
  f: ChatbotInput;
  dat: DatTruong;
  suaDuoc: boolean;
}

export interface LuaChon {
  id: string;
  ten: string;
  /** Dòng phụ (trạng thái nguồn…), có thể rỗng. */
  phu: string;
}

/** Một khối trong tab. Không gấp được: tab đã làm việc gom nhóm, gấp thêm lần nữa là giấu hai lớp. */
export function Khoi({ ten, mo, children }: { ten?: string; mo?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-default p-4">
      {ten ? (
        <div>
          <h3 className="text-sm font-semibold text-content-primary">{ten}</h3>
          {mo ? <p className="mt-0.5 text-xs leading-relaxed text-content-secondary">{mo}</p> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/**
 * Nút chọn một trong vài phương án có mô tả (chế độ trả lời, kênh giao tiếp). Là `button aria-pressed` chứ không phải
 * radio trần vì mỗi phương án cần hai dòng chữ để người chọn hiểu hậu quả.
 */
export function NutLuaChon({
  chon, tat, nhan, mo, onChon,
}: {
  chon: boolean;
  tat: boolean;
  nhan: string;
  mo: string;
  onChon: () => void;
}) {
  return (
    <button
      type="button"
      disabled={tat}
      aria-pressed={chon}
      onClick={onChon}
      className={cn(
        'rounded border px-3 py-2 text-start transition disabled:opacity-60',
        chon ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600' : 'border-default hover:bg-subtle',
      )}
    >
      <span className="block text-xs font-medium text-content-primary">{nhan}</span>
      <span className="mt-0.5 block text-xs leading-snug text-content-secondary">{mo}</span>
    </button>
  );
}

/**
 * Ô nhập "mỗi dòng một mục" — tên miền, gốc nhúng.
 *
 * Giữ NGUYÊN VĂN đang gõ và chỉ báo lên danh sách đã tách. Bản cũ dựng lại nội dung ô từ danh sách đã lọc sau MỖI phím:
 * Enter tạo dòng rỗng → bị lọc → dấu xuống dòng biến mất dưới tay người gõ, không nhập được mục thứ hai. Rời ô thì trả về
 * dạng chuẩn hoá — lúc ấy không còn ai đang gõ để bị giật mất ký tự.
 */
export function ODanhSachDong({
  giaTri,
  onDoi,
  ...props
}: Omit<ComponentProps<typeof Textarea>, 'value' | 'onChange' | 'onBlur'> & {
  giaTri: string[];
  onDoi: (ds: string[]) => void;
}) {
  const [tho, setTho] = useState<string | null>(null);
  return (
    <Textarea
      {...props}
      value={tho ?? giaTri.join('\n')}
      onChange={(e) => {
        setTho(e.target.value);
        onDoi(tachDong(e.target.value));
      }}
      onBlur={() => setTho(null)}
    />
  );
}
